import React, {useCallback, useLayoutEffect, useMemo} from "react";
import {useRef, useEffect, useState} from "react";
import {
  ReactFlow,
  useNodesState,
  useEdgesState,
  Background,
  Node,
  Edge,
  ReactFlowProvider,
  ProOptions,
  getIncomers,
  getOutgoers,
  getConnectedEdges,
  useReactFlow,
  useStore,
  ConnectionMode,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {cn} from "@/lib/utils";
import {createNode} from "../NodeTypes/WorkflowNode";
import Settings from "./Settings";
import {motion, AnimatePresence} from "framer-motion";
import {startNodeBlueprint} from "../NodeTypes/Blueprints/start";
import {
  AssistantType,
  FlowMode,
  SavingState,
  WorkflowNodeData,
  WorkflowNodeTypes,
} from "@/types/node";

import useLayout from "@/hooks/useLayout";
import {createNodeTypes} from "../NodeTypes";
import {createEdgeTypes} from "../EdgeTypes";

import "@xyflow/react/dist/style.css";
import {useSelectedElements} from "@/hooks/useSelectedElements";
import Topbar from "./Topbar";
import {getFlow} from "../../../api";
import {rootNodeBlueprint} from "../NodeTypes/Blueprints";
import {Loader2} from "lucide-react";
import {WorkflowNodesData} from "../NodeTypes/NodesData";
import PromptDrawer from "./PromptDrawer";
import {globalNodeBlueprint} from "../NodeTypes/Blueprints/global";
import {
  applyFlowMigration,
  LATEST_FLOW_DATA_VERSION,
} from "@/lib/flowMigrations";
import useUndoRedo from "@/hooks/useUndoRedo";
import {
  findFlowDataIssues,
  SaveProvider,
  useSave,
} from "@/contexts/SaveContext";
import {Minimap} from "./Minimap";
import {Toaster} from "@/components/ui/toaster";
import {TooltipProvider} from "@/components/ui/tooltip";
import {FlowBuilderSettings} from "@/config/flowBuilderSettings";
import {PageLeaveGuard} from "./Modals/PageLeaveGuard";
import {TryAgainModal} from "./Modals/TryAgainModal";
import {CopyPasteProvider} from "@/contexts/CopyPasteContext";

const proOptions: ProOptions = {account: "paid-pro", hideAttribution: true};

interface CanvasProps {
  className: string;
}

const zoomSelector = s => s.transform[2] <= 0.4;

const Canvas: React.FC<CanvasProps> = ({className}) => {
  const {undo, redo, canUndo, canRedo, takeSnapshot, resetUndoRedo} =
    useUndoRedo();
  const {
    setSavingState,
    setHasUnsavedChanges,
    setFlowIssues,
    flowMode,
    flowData,
    setFlowData,
    setFlowMode,
  } = useSave();

  const [initialFlowData, setInitialFlowData] = useState<string | null>(null);
  const [assistantType, setAssistantType] = useState<AssistantType>(null);

  const isDemoMode = FlowBuilderSettings.demoMode;
  const [showPrompt, setShowPrompt] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showTryAgainModal, setShowTryAgainModal] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [flowDisabled, setFlowDisabled] = useState(false);

  const reactFlowWrapper = useRef(null);

  const {fitView, getViewport} = useReactFlow();
  const showContext = useStore(zoomSelector);

  const nodeTypes = useMemo(
    () => createNodeTypes(showContext, assistantType),
    [showContext, assistantType],
  );
  const edgeTypes = useMemo(
    () => createEdgeTypes(takeSnapshot),
    [takeSnapshot],
  );

  const defaultNodes: Node<WorkflowNodeData>[] = [
    createNode(rootNodeBlueprint),
    createNode(globalNodeBlueprint),
    createNode(startNodeBlueprint),
    {
      id: "2",
      data: {
        type: WorkflowNodeTypes.PLACEHOLDER,
      },
      position: {x: 0, y: 250},
      type: "placeholder",
    },
  ];

  const [nodes, setNodes, onNodesChange] = useNodesState<Node>(defaultNodes);

  const defaultEdges: Edge[] = useMemo(
    () => [
      {
        id: `root->${nodes?.[1]?.id}`,
        source: nodes?.[0]?.id,
        target: nodes?.[1]?.id,
        type: "workflow",
      },
      {
        id: `${nodes?.[1]?.id}->${nodes?.[2]?.id}`,
        source: nodes?.[1]?.id,
        target: nodes?.[2]?.id,
        type: "workflow",
      },
      {
        id: `${nodes?.[2]?.id}->2`,
        source: nodes?.[2]?.id,
        target: "2",
        type: "workflow",
      },
    ],
    [nodes],
  );

  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>(defaultEdges);

  const {getSelectedNode} = useSelectedElements();
  const [selectedNode, setSelectedNode] =
    useState<Node<WorkflowNodeData> | null>(null);

  const onMove = useCallback(() => {
    const zoom = getViewport().zoom;
    setZoomLevel(Math.round(zoom * 100));
  }, []);

  const onNodesDelete = useCallback(
    (deleted: Node[]) => {
      setEdges(
        deleted.reduce((acc, node) => {
          const connectedEdges = getConnectedEdges([node], edges);
          const filteredEdges = edges.filter(e => e.type !== "floating");

          const incomers = getIncomers(
            node as Node<WorkflowNodeData>,
            nodes,
            filteredEdges,
          );

          const outgoers = getOutgoers(
            node as Node<WorkflowNodeData>,
            nodes,
            filteredEdges,
          );

          // Remove edges connected to the deleted node
          const remainingEdges = acc.filter(
            edge => !connectedEdges.includes(edge),
          );

          // Check if the parent (incomer) has more than one child (outgoing connections) to ignore Placeholder nodes
          const createdEdges = incomers.flatMap(({id: source}) => {
            const siblings = edges.filter(edge => edge.source === source);
            if (siblings.length > 1) {
              return [];
            }

            return outgoers.map(({id: target}) => ({
              id: `${source}->${target}`,
              source,
              target,
              type: "workflow",
            }));
          });

          return [...remainingEdges, ...createdEdges];
        }, edges),
      );

      takeSnapshot();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [nodes, edges, takeSnapshot],
  );

  const restoreSettings = (flowData: {nodes: Node[]; edges: Edge[]}) => {
    return (flowData?.nodes || defaultNodes).map(
      (node: Node<WorkflowNodeData>) => {
        const blueprint = WorkflowNodesData[node.type].blueprint || {};
        const blueprintSettings = blueprint.data?.settings || [];

        const restoredSettings = {
          settings: blueprintSettings.map(setting => ({
            ...setting,
            params: setting.params?.map(param => ({
              ...param,
              value: node.data.settings?.[param.key] || param.value,
            })),
          })),
        };

        return {
          ...node,
          data: {
            ...node.data,
            ...restoredSettings,
          },
          hidden: node.type === WorkflowNodeTypes.ROOT,
          selected: false,
          position: {
            x: 0,
            y: 0,
          },
          measured: {
            height: 0,
            width: 0,
          },
        };
      },
    );
  };

  useEffect(() => {
    const selected = getSelectedNode() as Node<WorkflowNodeData> | null;

    setSelectedNode(prevSelected => {
      if (prevSelected !== selected) {
        return selected;
      }
      return prevSelected;
    });
  }, [nodes, edges]);

  useEffect(() => {
    const fetchFlow = (attempt = 1) => {
      getFlow({model_id: FlowBuilderSettings.modelId})
        .then(data => {
          const flowDataJson = data.response.json
            ? JSON.parse(data.response.json)
            : null;
          const flowDataJsonTest = data.response.json_test
            ? JSON.parse(data.response.json_test)
            : flowDataJson;

          const flowData = applyFlowMigration(flowDataJson);
          const flowDataTest = applyFlowMigration(flowDataJsonTest);

          const fetchedNodes = restoreSettings(flowData);
          const fetchedNodesTest = restoreSettings(flowDataTest);

          const fetchedEdges = flowData?.edges || defaultEdges;
          const fetchedEdgesTest = flowDataTest?.edges || defaultEdges;

          setNodes(fetchedNodes);
          setEdges(fetchedEdges);

          const initialFlowData = JSON.stringify(
            {nodes: fetchedNodesTest, edges: fetchedEdgesTest},
            (key, value) =>
              key === "selected" || key === "measured" || key === "position"
                ? undefined
                : value,
          );

          setInitialFlowData(initialFlowData);
          setSavingState(SavingState.SAVED);

          fitView({nodes: fetchedNodes});
          setAssistantType(data.response?.model_type);

          setFlowData({
            live: {
              version: flowData?.version || LATEST_FLOW_DATA_VERSION,
              nodes: fetchedNodes,
              edges: fetchedEdges,
            },
            test: {
              version: 1,
              nodes: fetchedNodesTest,
              edges: fetchedEdgesTest,
            },
          });
          // Let ReactFlow render before showing UI
          setTimeout(() => setIsLoading(false), 100);
        })
        .catch(error => {
          console.error(`Error fetching stages (attempt ${attempt}):`, error);
          const maxAttempts = 10;

          if (attempt < maxAttempts) {
            console.log("Retrying fetch...");
            fetchFlow(attempt + 1);
            return;
          }

          const fallbackNodes = defaultNodes.map(node => ({
            ...node,
            selected: false,
          }));

          if (error.message.includes("400")) {
            setFlowData({
              live: {
                version: 0,
                nodes: fallbackNodes,
                edges: defaultEdges,
              },
              test: {
                version: 0,
                nodes: fallbackNodes,
                edges: defaultEdges,
              },
            });

            setNodes(fallbackNodes);
            setEdges(defaultEdges);
            setSavingState(SavingState.SAVED);
          } else {
            setShowTryAgainModal(true);
            setHasUnsavedChanges(false);
            setFlowDisabled(true);
            setSavingState(SavingState.ERROR);
          }

          setTimeout(() => setIsLoading(false), 100);
        });
    };

    if (!FlowBuilderSettings.demoMode) {
      fetchFlow();
    } else {
      setNodes(FlowBuilderSettings.demoData.nodes);
      setEdges(FlowBuilderSettings.demoData.edges);
      setFlowData({
        live: {
          version: 0,
          nodes: FlowBuilderSettings.demoData.nodes,
          edges: FlowBuilderSettings.demoData.edges,
        },
        test: {
          version: 0,
          nodes: FlowBuilderSettings.demoData.nodes,
          edges: FlowBuilderSettings.demoData.edges,
        },
      });
      setFlowMode(FlowMode.LIVE);
      setIsLoading(false);
      setHasUnsavedChanges(false);
      setTimeout(() => {
        fitView({nodes: FlowBuilderSettings.demoData.nodes});
      }, 150);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (nodes.length === 0) return;
    setFlowIssues(findFlowDataIssues(nodes));
  }, [nodes]);

  useLayoutEffect(() => {
    if (flowMode) {
      setShowPrompt(false);
      // Reactflow has some kind of optimization that if the size and node ID is not changed,
      // then that node is not being updated, so by clearing the nodes and edges,
      // we force a re-render of all the nodes and edges
      setNodes([]);
      setEdges([]);

      requestAnimationFrame(() => {
        setNodes([...flowData[flowMode].nodes]);
        setEdges([...flowData[flowMode].edges]);
      });
    }
  }, [flowMode, flowData]);

  useEffect(() => {
    const selected = getSelectedNode() as Node<WorkflowNodeData> | null;
    if (selected !== selectedNode) {
      setSelectedNode(selected);
    }

    const currentFlowData = JSON.stringify(
      {nodes: nodes, edges: edges},
      (key, value) =>
        key === "selected" || key === "measured" || key === "position"
          ? undefined
          : value,
    );

    const hasUnsavedChanges =
      nodes.length > 0 && currentFlowData !== initialFlowData;

    setHasUnsavedChanges(hasUnsavedChanges);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes, edges, initialFlowData]);

  useLayout();

  return (
    <>
      {flowDisabled && (
        <div className="absolute top-0 left-0 w-full h-full bg-white/50 z-50"></div>
      )}
      {!isDemoMode && (
        <Topbar
          setShowPrompt={setShowPrompt}
          undoRedoDetails={{
            undo,
            redo,
            canUndo,
            canRedo,
            resetUndoRedo,
          }}
          disabled={isLoading}
        />
      )}
      <section className={cn("", className)} ref={reactFlowWrapper}>
        {isLoading ? (
          <div className="flex justify-center items-center h-full">
            <Loader2 className="size-10 animate-spin" />
          </div>
        ) : (
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            proOptions={proOptions}
            fitView
            deleteKeyCode={[]}
            nodesDraggable={false}
            zoomOnDoubleClick={true}
            maxZoom={1.5}
            minZoom={0.25}
            nodesConnectable={false}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodesDelete={onNodesDelete}
            connectionMode={ConnectionMode.Loose}
            onMove={onMove}
          >
            <Background bgColor="#fafafa" gap={8} size={1.5} color="#e8e8e8" />
            {FlowBuilderSettings.minimapEnabled && (
              <Minimap zoomLevel={zoomLevel} />
            )}
            <AnimatePresence>
              {selectedNode && !isDemoMode && (
                <motion.div
                  key="settings"
                  initial={{opacity: 0, x: 30}}
                  animate={{opacity: 1, x: 0}}
                  exit={{opacity: 0, x: 30}}
                  transition={{duration: 0.1, ease: "easeInOut"}}
                  className="absolute right-5 top-5 bottom-5 z-10"
                >
                  <Settings
                    selectedNode={selectedNode}
                    nodes={nodes}
                    takeSnapshot={takeSnapshot}
                    assistantType={assistantType}
                  />
                </motion.div>
              )}
              {showPrompt && !isDemoMode && (
                <>
                  <motion.div
                    key="prompt-drawer"
                    initial={{opacity: 0, x: 0}}
                    animate={{opacity: 1, x: 0}}
                    exit={{opacity: 0, x: 0}}
                    transition={{duration: 0.1, ease: "easeInOut"}}
                    className="absolute z-10 w-full h-full backdrop-blur-sm bg-[#16161D91]"
                    onClick={() => setShowPrompt(false)}
                  ></motion.div>
                  <motion.div
                    key="settings"
                    initial={{opacity: 0, x: 30}}
                    animate={{opacity: 1, x: 0}}
                    exit={{opacity: 0, x: 30}}
                    transition={{duration: 0.1, ease: "easeInOut"}}
                    className="absolute right-5 top-5 bottom-5 left-5 z-10 max-w-250 w-full ml-auto"
                  >
                    <PromptDrawer
                      nodes={nodes}
                      edges={edges}
                      setShowPrompt={setShowPrompt}
                    />
                  </motion.div>
                </>
              )}
            </AnimatePresence>
            <TryAgainModal
              showModal={showTryAgainModal}
              setShowModal={setShowTryAgainModal}
            />
          </ReactFlow>
        )}
      </section>
    </>
  );
};

const CanvasWithProvider: React.FC<CanvasProps> = ({className}) => {
  return (
    <TooltipProvider>
      <ReactFlowProvider>
        <SaveProvider>
          <CopyPasteProvider>
            <Canvas className={className} />
            <Toaster />
            <PageLeaveGuard />
          </CopyPasteProvider>
        </SaveProvider>
      </ReactFlowProvider>
    </TooltipProvider>
  );
};

export default CanvasWithProvider;
